import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Css/AddQuestion.css";

const chapterWiseQuestions = {
  Marathi: {
    "Chapter 1": {
      regular: [
        "सृष्टीतल्या या अद्भुत गोष्टी कोणत्या आहेत ?",
        "पर्यावरण या शब्दाचा सखोल अर्थ स्पष्ट करा.",
        "शूर या शब्दाचा अर्थ स्पष्ट करा.",
        "शिक्षणाचे महत्त्व स्पष्ट करा.",
      ],
      paragraph: [
        {
          title: "पर्यावरणाची गरज",
          questions: [
            "पर्यावरणाचे रक्षण का आवश्यक आहे?",
            "आपण पर्यावरणासाठी काय करू शकतो?",
            "अभ्यासाव्यतिरिक्त काय शिकले?",
          ],
        },
        {
          title: "पर्यावरण संवर्धन",
          questions: [
            "पर्यावरणाचे रक्षण का आवश्यक आहे?",
            "आपण पर्यावरणासाठी काय करू शकतो?",
            "विचार करा आणि उत्तर द्या.",
          ],
        },
      ],
    },
    "Chapter 2": {
      regular: [
        "ज्ञान या संज्ञा शब्दाचे शब्दयोग लिहा.",
        "स्वच्छता या शब्दाचा विपरीतार्थी शब्द लिहा.",
        "विचार करा आणि उत्तर द्या.",
      ],
      paragraph: [
        {
          title: "मराठी भाषेचे स्थान",
          questions: [
            "मराठी भाषेचे महत्त्व सांगा.",
            "भाषा जपण्यासाठी आपण काय करू शकतो?",
            "विचार करा आणि उत्तर द्या.",
          ],
        },
      ],
    },
  },
  English: {
    "Chapter 1": {
      regular: [
        "What is the moral of the story 'The Clever Fox'?",
        "Explain the meaning of the proverb 'Honesty is the best policy'.",
        "Write a few lines about your best friend.",
        "Describe your school in 5 sentences.",
      ],
      paragraph: [
        {
          title: "The Brave Little Kite",
          questions: [
            "What did the little kite learn from the big kite?",
            "How did the little kite feel when it started flying?",
            "What is the central message of the story?",
          ],
        },
        {
          title: "The Helpful Boy",
          questions: [
            "Why was the boy called helpful?",
            "Describe one situation where the boy helped someone.",
            "What lesson do you learn from this story?",
          ],
        },
      ],
    },
    "Chapter 2": {
      regular: [
        "Define a noun with examples.",
        "What are the different types of verbs?",
        "Form a sentence using the word 'beautiful'.",
      ],
      paragraph: [
        {
          title: "Grammar Fun",
          questions: [
            "What is a verb? Give 2 examples.",
            "How can we use adjectives in a sentence?",
            "Write 3 sentences using pronouns.",
          ],
        },
      ],
    },
  },
};

const AddQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState("Marathi");
  const [chapter, setChapter] = useState("Chapter 1");
  const [tab, setTab] = useState("paragraph");

  const [selectedBySubject, setSelectedBySubject] = useState({
    Marathi: { "Chapter 1": [], "Chapter 2": [] },
    English: { "Chapter 1": [], "Chapter 2": [] },
  });

  const allSelected = useMemo(
    () =>
      Object.values(selectedBySubject).flatMap((subjectChapters) =>
        Object.values(subjectChapters).flat(),
      ),
    [selectedBySubject],
  );

  const totalSelected = allSelected.length;
  const selectedInThisSubject = Object.values(
    selectedBySubject[subject] || {},
  ).flat();
  const selectedCountThisSubject = selectedInThisSubject.length;
  const selectedInThisChapter = selectedBySubject[subject]?.[chapter] || [];
  const selectedCountThisChapter = selectedInThisChapter.length;

  const SUBJECT_LIMIT = 5;
  const GLOBAL_LIMIT = 40;
  const isSubjectLimitReached = selectedCountThisSubject >= SUBJECT_LIMIT;
  const isGlobalLimitReached = totalSelected >= GLOBAL_LIMIT;

  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [allRegularQuestions, setAllRegularQuestions] = useState([]);
  const [paragraphQuestions, setParagraphQuestions] = useState([]);

  useEffect(() => {
    const chapterData = chapterWiseQuestions[subject]?.[chapter];
    if (!chapterData) {
      setAvailableQuestions([]);
      setAllRegularQuestions([]);
      setParagraphQuestions([]);
      return;
    }

    const selectedRegularTextsInThisChapter = new Set(
      selectedInThisChapter
        .filter((it) => it.type === "regular")
        .map((it) => it.text),
    );

    setAllRegularQuestions(chapterData.regular || []);
    setAvailableQuestions(
      (chapterData.regular || []).filter(
        (q) => !selectedRegularTextsInThisChapter.has(q),
      ),
    );
    setParagraphQuestions(chapterData.paragraph || []);
  }, [subject, chapter, selectedBySubject]);

  const isGloballySelected = (text) =>
    allSelected.some((it) => it.text === text);

  const handleAddQuestion = (question) => {
    if (isSubjectLimitReached || isGlobalLimitReached) return;
    if (isGloballySelected(question)) return;

    setSelectedBySubject((prev) => ({
      ...prev,
      [subject]: {
        ...prev[subject],
        [chapter]: [
          ...(prev[subject]?.[chapter] || []),
          {
            id: `${subject}|${chapter}|regular|${question}`,
            text: question,
            type: "regular",
            chapter,
            subject,
          },
        ],
      },
    }));
  };

  const handleAddParagraph = (para) => {
    if (isSubjectLimitReached || isGlobalLimitReached) return;

    const notSelected = para.questions.filter((q) => !isGloballySelected(q));
    if (notSelected.length === 0) return;

    const globalSpace = Math.max(0, GLOBAL_LIMIT - totalSelected);
    if (globalSpace <= 0) return;

    const subjectSpace = Math.max(0, SUBJECT_LIMIT - selectedCountThisSubject);
    if (subjectSpace <= 0) return;

    const toAdd = notSelected.slice(0, Math.min(globalSpace, subjectSpace));
    if (!toAdd.length) return;

    setSelectedBySubject((prev) => ({
      ...prev,
      [subject]: {
        ...prev[subject],
        [chapter]: [
          ...(prev[subject]?.[chapter] || []),
          ...toAdd.map((q) => ({
            id: `${subject}|${chapter}|paragraph|${para.title}|${q}`,
            text: q,
            type: "paragraph",
            paraTitle: para.title,
            chapter,
            subject,
          })),
        ],
      },
    }));
  };

  const handleRemoveQuestion = (index) => {
    const item = allSelected[index];
    if (!item) return;

    setSelectedBySubject((prev) => {
      const next = { ...prev };
      next[item.subject][item.chapter] = (
        next[item.subject]?.[item.chapter] || []
      ).filter((x) => x.id !== item.id);
      return next;
    });
  };

  const handlePreviewClick = () => {
    navigate("/dashboard/previewpaper");
  };

  const availableChapters = Object.keys(chapterWiseQuestions[subject] || {});

  return (
    <div className="builder-container">
      <div className="page-header">
        <h3 style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
          परीक्षा व्यवस्थापन &gt;&gt;परीक्षा तयार करा &gt;&gt;{" "}
          <span>प्रश्न जोडा </span>
        </h3>

        <div className="edit-btnn">
          <button
            className="builder-container-preview-btn"
            onClick={handlePreviewClick}
            disabled={totalSelected === 0}
          >
            प्रिव्ह्यू
          </button>
        </div>
      </div>
      <div className="builder-container-header">
        <p>
          <strong>परीक्षेचे नाव:</strong> पूर्व उ. प्रा. शिष्यवृत्ती परीक्षा
          &nbsp;&nbsp;
          <strong>इयत्ता:</strong> ५ वी &nbsp;&nbsp;
          <strong>एकूण गुण :</strong> २५
        </p>
      </div>

      <div className="crt-exam">
        <div>
          <select
            className="chapter-select"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              const chapters = Object.keys(
                chapterWiseQuestions[e.target.value] || {},
              );
              setChapter(chapters[0] || "");
            }}
          >
            <option value="sub">विषय</option>
            <option value="Marathi">मराठी</option>
            <option value="English">गणित</option>
          </select>
        </div>

        <select
          className="chapter-select"
          value={chapter}
          onChange={(e) => setChapter(e.target.value)}
          disabled={isSubjectLimitReached}
        >
          <option> घटक </option>
          <option>घटक 1</option>
          <option>घटक 2</option>
        </select>

        <select
          className="chapter-select"
          value={chapter}
          onChange={(e) => setChapter(e.target.value)}
          disabled={isSubjectLimitReached}
        >
          <option>उपघटक</option>
          <option>उपघटक 1</option>
          <option>उपघटक 2</option>
        </select>
      </div>

      <div className="tabs">
        <button
          className={tab === "paragraph" ? "active-tab" : ""}
          onClick={() => setTab("paragraph")}
          disabled={isSubjectLimitReached}
        >
          परिच्छेद प्रश्न
        </button>
        <button
          className={tab === "regular" ? "active-tab" : ""}
          onClick={() => setTab("regular")}
          disabled={isSubjectLimitReached}
        >
          रेग्युलर प्रश्न
        </button>
      </div>

      <div className="main-contentt">
        {tab === "paragraph" && (
          <div className="paragraph-questions">
            <h3 className="que-head">Paragraphs</h3>
            {paragraphQuestions.length === 0 ? (
              <p className="empty-msg">
                {isSubjectLimitReached
                  ? "Subject limit reached"
                  : "Please select a chapter to view paragraph questions."}
              </p>
            ) : (
              paragraphQuestions.map((para, idx) => {
                const paraQuestionsAvailable = para.questions.filter(
                  (q) => !isGloballySelected(q),
                ).length;

                return (
                  <div
                    key={`${subject}|${chapter}|para|${idx}`}
                    className="paragraph-box"
                  >
                    <div className="para-svg-sep">
                      <h4>{para.title}</h4>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="para-svg"
                        width="20"
                        height="14"
                        viewBox="0 0 22 16"
                        fill="none"
                      >
                        <path
                          d="M11 12.5C12.25 12.5 13.3125 12.0625 14.1875 11.1875C15.0625 10.3125 15.5 9.25 15.5 8C15.5 6.75 15.0625 5.6875 14.1875 4.8125C13.3125 3.9375 12.25 3.5 11 3.5C9.75 3.5 8.6875 3.9375 7.8125 4.8125C6.9375 5.6875 6.5 6.75 6.5 8C6.5 9.25 6.9375 10.3125 7.8125 11.1875C8.6875 12.0625 9.75 12.5 11 12.5ZM11 10.7C10.25 10.7 9.6125 10.4375 9.0875 9.9125C8.5625 9.3875 8.3 8.75 8.3 8C8.3 7.25 8.5625 6.6125 9.0875 6.0875C9.6125 5.5625 10.25 5.3 11 5.3C11.75 5.3 12.3875 5.5625 12.9125 6.0875C13.4375 6.6125 13.7 7.25 13.7 8C13.7 8.75 13.4375 9.3875 12.9125 9.9125C12.3875 10.4375 11.75 10.7 11 10.7ZM11 15.5C8.56667 15.5 6.35 14.8208 4.35 13.4625C2.35 12.1042 0.9 10.2833 0 8C0.9 5.71667 2.35 3.89583 4.35 2.5375C6.35 1.17917 8.56667 0.5 11 0.5C13.4333 0.5 15.65 1.17917 17.65 2.5375C19.65 3.89583 21.1 5.71667 22 8C21.1 10.2833 19.65 12.1042 17.65 13.4625C15.65 14.8208 13.4333 15.5 11 15.5ZM11 13.5C12.8833 13.5 14.6125 13.0042 16.1875 12.0125C17.7625 11.0208 18.9667 9.68333 19.8 8C18.9667 6.31667 17.7625 4.97917 16.1875 3.9875C14.6125 2.99583 12.8833 2.5 11 2.5C9.11667 2.5 7.3875 2.99583 5.8125 3.9875C4.2375 4.97917 3.03333 6.31667 2.2 8C3.03333 9.68333 4.2375 11.0208 5.8125 12.0125C7.3875 13.0042 9.11667 13.5 11 13.5Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                    <ol className="mul-para">
                      {para.questions.slice(0, 3).map((q, i) => (
                        <li
                          className={`mul-que ${isGloballySelected(q) ? "selected-question" : ""}`}
                          key={`${subject}|${chapter}|para|${idx}|q|${i}`}
                        >
                          {q}
                          {isGloballySelected(q) && (
                            <span className="selected-tag">(Selected)</span>
                          )}
                        </li>
                      ))}
                    </ol>
                    <button
                      onClick={() => handleAddParagraph(para)}
                      disabled={
                        isSubjectLimitReached || paraQuestionsAvailable === 0
                      }
                      title={
                        isSubjectLimitReached
                          ? `Subject limit reached (${SUBJECT_LIMIT})`
                          : paraQuestionsAvailable === 0
                            ? "All questions already selected"
                            : ""
                      }
                    >
                      Add Paragraph (+{paraQuestionsAvailable} Questions)
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === "regular" && (
          <div className="questions-panel">
            <h3 className="que-head">Regular</h3>
            {availableQuestions.length === 0 ? (
              <p className="empty-msg">
                {isSubjectLimitReached
                  ? "Subject limit reached"
                  : "No available questions or all questions already selected"}
              </p>
            ) : (
              availableQuestions.map((q, index) => {
                const globallySelected = isGloballySelected(q);
                const disabled = isSubjectLimitReached || globallySelected;
                const title = isSubjectLimitReached
                  ? `Subject limit reached (${SUBJECT_LIMIT})`
                  : globallySelected
                    ? "Already added from another chapter/subject"
                    : "";
                return (
                  <button
                    key={`${subject}|${chapter}|reg|${index}`}
                    className={`question-button ${globallySelected ? "selected-question" : ""}`}
                    onClick={() => handleAddQuestion(q)}
                    disabled={disabled}
                    title={title}
                  >
                    {q}
                    {globallySelected && (
                      <span className="selected-tag">(Selected)</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}

        <div className="selected-panel">
          <div className="selected-title">
            <span>
              गुण - {totalSelected * 2}/{GLOBAL_LIMIT}
            </span>
          </div>
          <div className="selected-list">
            {totalSelected === 0 && (
              <div className="drop-placeholder">Drop Here</div>
            )}
            {allSelected.map((item, index) => (
              <div key={item.id} className="selected-item">
                <div className="que-add-span">
                  <span className="que-span">{item.text}</span>
                </div>
                <div className="que-remove">
                  <span className="marks-span">2 Marks</span>
                  <button
                    className="selected-list-remove-btn"
                    onClick={() => handleRemoveQuestion(index)}
                  >
                    -
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isSubjectLimitReached && (
        <p className="limit-warning">
          Note: {subject} Subject Reached Their Limit
          {totalSelected < GLOBAL_LIMIT
            ? " Please Select Another Subject."
            : ""}
        </p>
      )}

      {isGlobalLimitReached && (
        <p className="limit-warning">
          Note: Total questions limit reached ({GLOBAL_LIMIT} questions).
        </p>
      )}
    </div>
  );
};

export default AddQuestion;
